import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function EmotionalSection() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const stats = [
    { valueKey: 'emotional.stat1.value', labelKey: 'emotional.stat1.label' },
    { valueKey: 'emotional.stat2.value', labelKey: 'emotional.stat2.label' },
    { valueKey: 'emotional.stat3.value', labelKey: 'emotional.stat3.label' },
  ];

  return (
    <section
      id="emotional"
      ref={ref}
      className="relative overflow-hidden py-28 md:py-40 px-4 md:px-8 bg-brand-black"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/6228b5cf8db28a1d2383d286652cd15c.jpg"
          alt="Worship atmosphere"
          className="w-full h-full object-cover object-top opacity-30"
        />
      </div>

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-brand-black/60 via-brand-black/40 to-brand-black/70" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.1)_0%,transparent_65%)]" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />

      <div className="relative z-10 max-w-4xl mx-auto text-center">
        {/* Decorative icon */}
        <div
          className={`flex justify-center mb-8 transition-all duration-700 ${
            visible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
          }`}
        >
          <div className="w-14 h-14 flex items-center justify-center">
            <i className="ri-heart-3-line text-4xl text-gold" />
          </div>
        </div>

        {/* Main quote */}
        <div
          className={`transition-all duration-1000 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="font-heading font-black text-2xl md:text-4xl lg:text-5xl text-brand-white leading-tight mb-4">
            {t('emotional.quote1')}
          </p>
          <p className="font-heading font-black text-2xl md:text-4xl lg:text-5xl text-gold leading-tight mb-8">
            {t('emotional.quote2')}
          </p>
          <p className="font-heading font-black text-2xl md:text-4xl lg:text-5xl text-brand-white leading-tight">
            {t('emotional.quote3')}
          </p>
        </div>

        {/* Divider */}
        <div
          className={`flex items-center justify-center my-10 gap-4 transition-all duration-700 delay-500 ${
            visible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="w-16 h-px bg-gold/40" />
          <div className="w-2 h-2 rounded-full bg-gold" />
          <div className="w-16 h-px bg-gold/40" />
        </div>

        {/* Secondary text */}
        <div
          className={`transition-all duration-700 delay-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="font-body text-base md:text-xl text-brand-white/70 leading-relaxed max-w-2xl mx-auto mb-2">
            {t('emotional.sub1')}
          </p>
          <p className="font-body text-base md:text-xl text-brand-white/70 leading-relaxed max-w-2xl mx-auto">
            {t('emotional.sub2')} <span className="text-gold font-semibold">{t('emotional.highlight')}</span>
          </p>
        </div>

        {/* Stats row */}
        <div
          className={`grid grid-cols-3 gap-4 md:gap-6 mt-12 md:mt-16 transition-all duration-700 delay-900 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {stats.map((stat) => (
            <div key={stat.labelKey} className="flex flex-col items-center gap-1">
              <span className="font-heading font-black text-2xl md:text-4xl text-gold">{t(stat.valueKey)}</span>
              <span className="font-body text-xs text-brand-white/50 tracking-wider uppercase text-center leading-tight">{t(stat.labelKey)}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
